import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Badge from '@mui/material/Badge';
import Collapse from '@mui/material/Collapse';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoIcon from '@mui/icons-material/Info';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import EastIcon from '@mui/icons-material/East';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import DeleteIcon from '@mui/icons-material/Delete';
import { selectPendingChanges, clearPendingChanges, removeOperationFromPending, selectPatches } from '../patchesSlice';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';

interface OperationValue {
  name?: string;
  internalName?: string;
  id?: string;
  [key: string]: any;
}

interface Operation {
  __typename: string;
  [key: string]: string | number | boolean | OperationValue | null;
}

interface Patch {
  patchId: string;
  storeNumber: string;
  [key: string]: any;
}

interface PendingChangesViewProps {
  open: boolean;
  onClose: () => void;
}

const OperationTreeItem: React.FC<{ 
  operation: Operation; 
  nodeId: string; 
  index: number; 
  operationType: 'set' | 'remove';
  onDeleteOperation: (operationType: 'set' | 'remove', index: number) => void;
  patches: Patch[];
  patchIds: string[];
}> = ({
  operation,
  nodeId,
  index,
  operationType,
  onDeleteOperation,
  patches,
  patchIds,
}) => {
  // Get store numbers for this specific operation
  const getOperationStores = () => {
    // If the operation has its own affectedStores array (from deduplication), use that
    if (operation.affectedStores && Array.isArray(operation.affectedStores)) {
      return operation.affectedStores;
    }
    // Fallback to the previous logic for operations without affectedStores
    const storeNumbers: string[] = [];
    patchIds.forEach(patchId => {
      const patch = patches.find(p => p.patchId === patchId);
      if (patch?.storeNumber && !storeNumbers.includes(patch.storeNumber)) {
        storeNumbers.push(patch.storeNumber);
      }
    });
    return storeNumbers.sort();
  };

  const operationStores = getOperationStores();

  // Helper to get display label for a bundle
  const getBundleLabel = (bundle: any) => {
    if (!bundle) return 'Unknown Bundle';
    if (bundle.displayName) return bundle.displayName;
    if (bundle.internalName && bundle.bundleCode) return `${bundle.internalName} (${bundle.bundleCode})`;
    if (bundle.name && bundle.bundleCode) return `${bundle.name} (${bundle.bundleCode})`;
    return bundle.name || bundle.internalName || 'Unknown Bundle';
  };

  // Helper to get operation action description for all operations
  const getOperationActionDescription = (operationType: string, isRemoveOp: boolean) => {
    if (isRemoveOp) {
      // Remove operations - show what's being removed
      switch (operationType) {
        // SetProdPrices operations in remove array
        case 'SetVariantPriceMenuPatchOperation':
          return 'remove variant price';
        case 'SetModifierWeightPriceMenuPatchOperation':
          return 'remove modifier weight price';
        case 'SetModifierPriceRangesMenuPatchOperation':
          return 'remove modifier price ranges';
        
        // SetBundPrice operations in remove array  
        case 'SetBundlePriceMenuPatchOperation':
          return 'remove bundle price';
        
        // RmvProd operations in remove array (removing exclusions)
        case 'RemoveProductMenuPatchOperation':
          return 'remove product exclusion';
        case 'RemoveVariantMenuPatchOperation':
          return 'remove variant exclusion';
        case 'RemoveModifierFromSlotPatchOperation':
          return 'remove modifier exclusion from slot';
        case 'RemoveModifierMenuPatchOperation':
          return 'remove modifier exclusion';
        case 'SetModifierWeightIsExcludedFromVariantMenuPatchOperation':
          return 'remove modifier weight exclusion from variant';
        
        // RmvBund operations in remove array (removing exclusions)
        case 'RemoveBundleMenuPatchOperation':
          return 'remove bundle exclusion';
        
        default:
          return 'remove operation';
      }
    } else {
      // Set operations - show what's being set/added
      switch (operationType) {
        // SetProdPrices operations in set array (setting prices)
        case 'SetVariantPriceMenuPatchOperation':
          return null; // Will show "set variant price → $X.XX" via price display
        case 'SetModifierWeightPriceMenuPatchOperation':
          return null; // Will show "set modifier weight price → $X.XX" via price display
        case 'SetModifierPriceRangesMenuPatchOperation':
          return null; // Will show "set modifier price ranges → $X.XX-$Y.YY" via price display
        
        // SetBundPrice operations in set array (setting prices)
        case 'SetBundlePriceMenuPatchOperation':
          return null; // Will show "set bundle price → $X.XX" via price display
        
        // RmvProd operations in set array (adding exclusions)
        case 'RemoveProductMenuPatchOperation':
          return 'add product exclusion';
        case 'RemoveVariantMenuPatchOperation':
          return 'add variant exclusion';
        case 'RemoveModifierFromSlotPatchOperation':
          return 'add modifier exclusion from slot';
        case 'RemoveModifierMenuPatchOperation':
          return 'add modifier exclusion';
        case 'SetModifierWeightIsExcludedFromVariantMenuPatchOperation':
          return 'add modifier weight exclusion to variant';
        
        // RmvBund operations in set array (adding exclusions)
        case 'RemoveBundleMenuPatchOperation':
          return 'add bundle exclusion';
        
        default:
          return 'set operation';
      }
    }
  };

  const renderOperationDetails = (op: Operation) => {
    const details = [];
    const productDetails = (
      typedValue: OperationValue,
      authoringPrice: string,
      key: string,
      index: number,
      altBody: boolean
    ) => {
      // Safety check - ensure typedValue is an object
      if (!typedValue || typeof typedValue !== 'object') {
        return null;
      }

      const isExclusionOp = [
        'RemoveBundleMenuPatchOperation',
        'RemoveProductMenuPatchOperation',
        'RemoveVariantMenuPatchOperation',
        'RemoveModifierFromSlotPatchOperation',
        'RemoveModifierMenuPatchOperation',
        'SetModifierWeightIsExcludedFromVariantMenuPatchOperation'
      ].includes(op.__typename);

      const isProduct = !!typedValue.product;
      const isBundle = !!typedValue.bundle;
      const isVariant = !!typedValue.variant;
      const isModifier = !!typedValue.modifier && typedValue.__typename !== 'RemoveModifierFromSlotPatchOperation';
      const isSlot = !!typedValue.slot;

      let mainText = 'Unknown Item';
      
      if (isProduct) {
        mainText = typedValue.product?.name || typedValue.product?.internalName || 'Unknown Product';
      } else if (isBundle) {
        mainText = getBundleLabel(typedValue.bundle);
      } else if (isVariant) {
        // For variant operations (like setVariantPrice), show product-variant combination
        const productName = typedValue.product?.name || typedValue.product?.internalName || 'Unknown Product';
        let variantName = '';
        if (Array.isArray(typedValue.variant?.selectedOptions) && typedValue.variant.selectedOptions.length > 0) {
          variantName = typedValue.variant.selectedOptions.map((so: any) => so?.optionValue?.internalName || so?.optionValue?.name || '').filter(Boolean).join(', ') || 'Variant';
        } else {
          variantName = typedValue.variant?.name || typedValue.variant?.internalName || 'Base Variant';
        }
        mainText = `${productName} - ${variantName}`;
      } else if (isModifier) {
        mainText = typedValue.modifier?.name || typedValue.modifier?.internalName || 'Unknown Modifier';
      } else if (isSlot) {
        mainText = typedValue.slot?.name || typedValue.slot?.internalName || typedValue.slot?.slotCode || typedValue.slotCode || 'Unknown Slot';
      }

      // Special handling for RemoveModifierFromSlotPatchOperation - show slot name as main text
      if (op.__typename === 'RemoveModifierFromSlotPatchOperation') {
        const slotName = typedValue.slot?.name || typedValue.slot?.internalName || 'Unknown Slot';
        mainText = slotName;
      }

      // Special handling for RemoveModifierMenuPatchOperation - show modifier info
      if (op.__typename === 'RemoveModifierMenuPatchOperation') {
        const modifierName = typedValue.modifier?.name || typedValue.modifier?.internalName || 'Unknown Modifier';
        mainText = modifierName;
      }

      // Special handling for SetModifierWeightIsExcludedFromVariantMenuPatchOperation - show just product name
      if (op.__typename === 'SetModifierWeightIsExcludedFromVariantMenuPatchOperation') {
        const productName = typedValue.product?.name || typedValue.product?.internalName || 'Unknown Product';
        mainText = productName;
      }
      const bundleStyle = isBundle ? { display: 'flex', flexDirection: 'row', justifyContent: 'space-between' } : {};
      return (
        <Box sx={{ ...bundleStyle }}>
          <Typography component="div">{mainText}</Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
              {bodyDetails(typedValue, altBody, isProduct)}
              {weightDetails(typedValue, key, index)}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!typedValue.weightPriceRanges && !!authoringPrice && !isBundle && !isExclusionOp && (
                <Typography component="div">
                  {operationType === 'remove' ? 'remove' : 'set'}{' '}
                  {op.__typename === 'SetVariantPriceMenuPatchOperation' ? 'variant price' :
                   op.__typename === 'SetModifierWeightPriceMenuPatchOperation' ? 'modifier weight price' :
                   op.__typename === 'SetModifierPriceRangesMenuPatchOperation' ? 'modifier price ranges' :
                   'price'}{' '}
                  {operationType === 'set' && <><EastIcon fontSize='small' sx={{ mb: -0.75 }} /> {authoringPrice}</>}
                </Typography>
              )}
              {/* Show operation action description for all operations based on type */}
              {(() => {
                const description = getOperationActionDescription(op.__typename, operationType === 'remove');
                return description ? (
                  <Typography component="div" sx={{ 
                    color: operationType === 'remove' ? 'error.main' : 'inherit', 
                    fontWeight: operationType === 'remove' ? 'medium' : 'normal'
                  }}>
                    {description}
                  </Typography>
                ) : null;
              })()}
              {isExcludedDetails(typedValue)}
            </Box>
          </Box>
          {!typedValue.weightPriceRanges && !!authoringPrice && isBundle && !isExclusionOp && (
            <Typography component="div">
              {operationType === 'remove' ? 'remove bundle price' : 
               <>set bundle price <EastIcon fontSize='small' sx={{ mb: -0.75 }} /> {authoringPrice}</>}
            </Typography>
          )}
          {/* Show operation action description for bundle operations */}
          {(() => {
            const description = getOperationActionDescription(op.__typename, operationType === 'remove');
            return description && isBundle ? (
              <Typography component="div" sx={{ 
                color: operationType === 'remove' ? 'error.main' : 'inherit', 
                fontWeight: operationType === 'remove' ? 'medium' : 'normal'
              }}>
                {description}
              </Typography>
            ) : null;
          })()}
        </Box>
      );
    };

    const bodyDetails = (typedValue: OperationValue, altBody: boolean, isProduct: boolean) => {
      // Special handling for SetModifierWeightIsExcludedFromVariantMenuPatchOperation
      if (op.__typename === 'SetModifierWeightIsExcludedFromVariantMenuPatchOperation') {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SubdirectoryArrowRightIcon sx={{ mb: -1 }} />
              {variantDetails(typedValue)}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {slotDetails(typedValue)}
              {modifierDetails(typedValue)}
              {singleWeightDetails(typedValue)}
            </Box>
          </Box>
        );
      }

      if (!isProduct) {
        if (op.__typename === 'RemoveModifierFromSlotPatchOperation') {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SubdirectoryArrowRightIcon sx={{ mb: -1 }} />
              {modifierDetails(typedValue)}
            </Box>
          );
        }
        if (op.__typename === 'RemoveModifierMenuPatchOperation') {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SubdirectoryArrowRightIcon sx={{ mb: -1 }} />
              {modifierDetails(typedValue)}
            </Box>
          );
        }
        return null;
      }
      return !altBody ? (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {typedValue.variant && <SubdirectoryArrowRightIcon sx={{ mb: -1 }} />}
          {variantDetails(typedValue)}
          {slotDetails(typedValue)}
          {modifierDetails(typedValue)}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SubdirectoryArrowRightIcon sx={{ mb: -1 }} />
            {variantDetails(typedValue)}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {slotDetails(typedValue)}
            {modifierDetails(typedValue)}
          </Box>
        </Box>
      );
    };

    const variantDetails = (typedValue: OperationValue) => {
      if (!typedValue.variant) return null;
      // Build label from product and variant option values
      const productName = typedValue.product?.name || typedValue.product?.internalName || '';
      let variantLabel = '';
      if (Array.isArray(typedValue.variant?.selectedOptions) && typedValue.variant.selectedOptions.length > 0) {
        variantLabel = typedValue.variant.selectedOptions.map((so: any) => so?.optionValue?.internalName || so?.optionValue?.name || '').filter(Boolean).join(', ') || 'Variant';
      } else {
        variantLabel = typedValue.variant?.name || typedValue.variant?.internalName || 'Base Variant';
      }
      const label = productName ? `${productName} - ${variantLabel}` : variantLabel;
      return (
        <Box>
          <Typography variant='body2'>Variant</Typography>
          <Chip label={label} sx={{ mr: 1 }} />
        </Box>
      );
    };

    const slotDetails = (typedValue: OperationValue) => {
      if (!typedValue.slot) return null;
      const label = typedValue.slot?.name || typedValue.slot?.internalName || 'Unknown Slot';
      return (
        <Box>
          <Typography variant='body2'>Slot</Typography>
          <Chip label={label} sx={{ mr: 1 }} />
        </Box>
      );
    };

    const modifierDetails = (typedValue: OperationValue) => {
      if (!typedValue.modifier) return null;
      const modifierName = typedValue.modifier?.name || typedValue.modifier?.internalName || 'Unknown Modifier';
      return (
        <Box>
          <Typography variant='body2'>Modifier</Typography>
          <Chip label={modifierName} sx={{ mr: 1 }} />
        </Box>
      );
    };

    const weightDetails = (typedValue: OperationValue, key: string, index: number) => {
      if (!typedValue.weightPriceRanges) return null;
      return (
        <Box>
          {typedValue.weightPriceRanges.map((weightPriceRange: any, rangeIdx: number) => (
            <Typography
              key={`range-${weightPriceRange.rangeStart}-${weightPriceRange.rangeEnd}-${index}-${rangeIdx}`}
            >
              Range {weightPriceRange.rangeStart} to{' '}
              {weightPriceRange.rangeEnd ? (
                weightPriceRange.rangeEnd
              ) : (
                <AllInclusiveIcon fontSize='small' sx={{ mb: -0.75, ml: 0.5 }} />
              )}{' '}
              {operationType === 'remove' ? 'remove modifier price range' : 
               <>set modifier price range <EastIcon fontSize='small' sx={{ mb: -0.75 }} /> $
               {weightPriceRange.authoringPrice.amount}</>}
            </Typography>
          ))}
        </Box>
      );
    };

    const singleWeightDetails = (typedValue: OperationValue) => {
      if (!typedValue.weight) return null;
      const weightName = typedValue.weight?.name || typedValue.weight?.internalName || 'Unknown Weight';
      return (
        <Box>
          <Typography variant='body2'>Weight</Typography>
          <Chip label={weightName} sx={{ mr: 1 }} />
        </Box>
      );
    };

    const isExcludedDetails = (typedValue: OperationValue) => {
      if (!typedValue || typeof typedValue !== 'object' || !('isExcluded' in typedValue)) return null;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant='body1'>Excluded: </Typography>
          <Chip label={typedValue.weight?.name || 'Unknown'} sx={{ ml: 1 }} />
        </Box>
      );
    };

    for (const [key, value] of Object.entries(op)) {
      // Skip metadata properties and non-object values
      if (key === '__typename' || 
          key === 'id' || 
          key === 'affectedStores' || 
          key === 'storeCount' || 
          key === 'originalIds' || 
          key === 'name' || 
          key === 'type' ||
          typeof value !== 'object' || 
          value === null) {
        continue;
      }
      const typedValue = value as OperationValue;
      const authoringPrice = typedValue.authoringPrice ? `$${typedValue.authoringPrice.amount}` : '';
      details.push(
        <React.Fragment key={`${key}-${index}`}>
          {productDetails(
            typedValue,
            authoringPrice,
            key,
            index,
            key === 'setModifierWeightPrice' || key === 'setModifierWeightIsExcludedFromVariant' ? true : false
          )}
        </React.Fragment>
      );
    }
    return <>{details}</>;
  };

  return (
    <TreeItem
      nodeId={nodeId}
      label={
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant='body2' component="div" sx={{ fontWeight: 'medium', width: '100%' }}>
              {renderOperationDetails(operation)}
            </Typography>
            {/* Store information per operation */}
            {operationStores.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant='caption' color='text.secondary'>
                  Stores ({operationStores.length}):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
                  {operationStores.slice(0, 5).map(storeNumber => (
                    <Chip 
                      key={storeNumber} 
                      label={storeNumber} 
                      size="small" 
                      variant="outlined"
                      sx={{ 
                        fontSize: '0.6rem', 
                        height: '16px',
                        '& .MuiChip-label': { px: 0.5 }
                      }}
                    />
                  ))}
                  {operationStores.length > 5 && (
                    <Chip 
                      label={`+${operationStores.length - 5}`}
                      size="small" 
                      variant="outlined"
                      sx={{ 
                        fontSize: '0.6rem', 
                        height: '16px',
                        '& .MuiChip-label': { px: 0.5 }
                      }}
                    />
                  )}
                </Box>
              </Box>
            )}
            <Divider sx={{ mt: 1 }} />
          </Box>
          <IconButton
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              onDeleteOperation(operationType, index);
            }}
            sx={{ ml: 1 }}
          >
            <DeleteIcon fontSize='small' />
          </IconButton>
        </Box>
      }
    />
  );
};

const PendingChangesView: React.FC<PendingChangesViewProps> = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const pendingChange = useSelector(selectPendingChanges);
  const patches = useSelector(selectPatches);
  const [expanded, setExpanded] = useState(false);
  const [showClearAllModal, setShowClearAllModal] = useState(false);

  // Toggle expansion of the change
  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  // Handle Clear All with confirmation
  const handleClearAllClick = () => {
    setShowClearAllModal(true);
  };

  const handleCancelClearAll = () => {
    setShowClearAllModal(false);
  };

  // Handle individual operation deletion
  const handleDeleteOperation = (operationType: 'set' | 'remove', operationIndex: number) => {
    dispatch(removeOperationFromPending({ operationType, operationIndex }));
  };

  if (!pendingChange.id) {
    return null;
  }

  return (
    <Paper variant='outlined'>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant='h6' sx={{ mr: 1 }}>
            Pending Change(s)
          </Typography>
          <Badge
            badgeContent={pendingChange.data.setOperations.length + pendingChange.data.removeOperations.length}
            color='primary'
            sx={{ mr: 1 }}
          >
            <InfoIcon fontSize='small' />
          </Badge>
          <Tooltip title='This change will be applied when you submit the patch'>
            <Typography variant='body2' color='text.secondary'>
              unsaved
            </Typography>
          </Tooltip>
        </Box>
        <Box>
          <Button 
            variant='outlined' 
            color='error' 
            size='small' 
            onClick={handleClearAllClick} 
            sx={{ mr: 1 }}
          >
            Clear All
          </Button>
        </Box>
      </Box>
      <Paper variant='outlined'>
        <List dense>
          <ListItem
            button
            onClick={handleToggleExpand}
            sx={{
              backgroundColor: expanded ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
              p: 1,
            }}
          >
            <ListItemText
              primary={pendingChange.type}
              secondary={
                <Typography variant="caption" display="block">
                  Set Operations: {pendingChange.data.setOperations.length}, Remove Operations: {pendingChange.data.removeOperations.length}
                </Typography>
              }
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: 'medium',
              }}
            />
            <ListItemSecondaryAction>
              <IconButton
                edge='end'
                aria-label='expand'
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleExpand();
                }}
                size='small'
              >
                {expanded ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
              </IconButton>
            </ListItemSecondaryAction>
          </ListItem>
          <Collapse in={expanded} timeout='auto' unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant='caption' display='block' gutterBottom>
                Change Details:
              </Typography>
              <Box
                sx={{
                  p: 1,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  overflow: 'auto',
                  maxHeight: '400px',
                }}
              >
                <TreeView
                  defaultCollapseIcon={<ExpandMoreIcon />}
                  defaultExpandIcon={<ChevronRightIcon />}
                  sx={{
                    height: 'auto',
                    minHeight: 200,
                    maxHeight: 400,
                    flexGrow: 1,
                    maxWidth: '100%',
                    overflowY: 'auto',
                  }}
                >
                  <TreeItem
                    nodeId='set-operations'
                    label={
                      <Typography variant='body2' sx={{ fontWeight: 'medium' }}>
                        Set Operations ({pendingChange.data.setOperations.length})
                      </Typography>
                    }
                  >
                    {pendingChange.data.setOperations.map((opObj, index) => {
                      // Type guard for new and legacy formats
                      let operation: any = opObj;
                      if (opObj && typeof opObj === 'object') {
                        if ('enriched' in opObj && opObj.enriched && opObj.enriched.__typename) {
                          operation = opObj.enriched;
                        } else if ('minimal' in opObj && opObj.minimal && opObj.minimal.__typename) {
                          operation = opObj.minimal;
                        }
                      }
                      return (
                        <OperationTreeItem
                          key={`set-${index}`}
                          nodeId={`set-op-${index}`}
                          operation={operation}
                          index={index}
                          operationType="set"
                          onDeleteOperation={handleDeleteOperation}
                          patches={patches}
                          patchIds={pendingChange.data.patchIds}
                        />
                      );
                    })}
                  </TreeItem>
                  <TreeItem
                    nodeId='remove-operations'
                    label={
                      <Typography variant='body2' sx={{ fontWeight: 'medium' }}>
                        Remove Operations ({pendingChange.data.removeOperations.length})
                      </Typography>
                    }
                  >
                    {pendingChange.data.removeOperations.map((opObj, index) => {
                      let operation: any = opObj;
                      if (opObj && typeof opObj === 'object') {
                        if ('enriched' in opObj && opObj.enriched && opObj.enriched.__typename) {
                          operation = opObj.enriched;
                        } else if ('minimal' in opObj && opObj.minimal && opObj.minimal.__typename) {
                          operation = opObj.minimal;
                        }
                      }
                      return (
                        <OperationTreeItem
                          key={`remove-${index}`}
                          nodeId={`remove-op-${index}`}
                          operation={operation}
                          index={index}
                          operationType="remove"
                          onDeleteOperation={handleDeleteOperation}
                          patches={patches}
                          patchIds={pendingChange.data.patchIds}
                        />
                      );
                    })}
                  </TreeItem>
                </TreeView>
              </Box>
            </Box>
          </Collapse>
        </List>
      </Paper>
      {/* Clear All Confirmation Modal */}
      <Dialog open={showClearAllModal} onClose={handleCancelClearAll}>
        <DialogTitle>Clear All Pending Changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clear all pending changes? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClearAll} color="primary">
            Cancel
          </Button>
          <Button onClick={() => { dispatch(clearPendingChanges()); setShowClearAllModal(false); }} color="error" variant="contained">
            Clear All
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PendingChangesView;